import { ColDef, GridOptions } from 'ag-grid-community'
import { NegotiationDetail, NegotiationPackageDetail, NegotiationStatus, NegotiationType, SummaryRowDirection } from 'api'
import * as constants from 'view/styles/constants'
import { Locale, Translation } from 'locale'
import {
  axeTradeConditionKeyCreator,
  createAxeTradeConditionRenderer,
  createGroupedActionRenderer,
  dateFilterParams,
  gridOptionsBase,
  StandardGridColumnId,
  StatusCellRenderer,
  tradesGridGroupingRowColDef,
  TypedContext,
  TypedRowNode,
  TypedValueFormatterParams,
  TypedValueGetterParams,
  ValidationCellRenderer,
  withAllContext
} from 'view/components/datagrid'
import { NumericInput } from 'view/components/inputs'
import { ClearingGridColumnId, CollateralBasketColumnId, CollectionColumnId } from 'view/routes/ColumnNames'
import {
  axeFilterValueFormatter,
  axeTradeConditionFilterTextGetter,
  booleanFormatter,
  callableTypeFormatter,
  canEditIncomingNegotiationDetailStartConsiderationOverride,
  canEditNegotiationDetailBondPrice,
  canEditNegotiationDetailCounterpartyId,
  canEditNegotiationDetailHaircut,
  canEditNegotiationDetailHaircutType,
  canEditNegotiationDetailNotional,
  canEditNegotiationDetailOtw,
  canEditNegotiationDetailRate,
  canEditNegotiationDetailStartConsideration,
  dateQuickFilterTextGetter,
  getClearingStatus,
  getHaircutFormatOptions,
  getNotionalFormatOptions,
  getOwnDirection,
  getRateFormatOptions,
  getStartConsiderationFormatOptions,
  getTargetValueIfNotOcoSummary,
  getTargetValueIfNotSummary,
  haircutTypeFormatter,
  isMultiDirectionSummary,
  makeCusipGetter,
  makeDateFormatter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberFormatterSummaryRow,
  makeNumberQuickFilterTextGetter,
  makeOrganisationFieldGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeStartConsiderationFilterGetter,
  makeStartConsiderationFormatter,
  makeTradingEntityFieldGetter,
  makeTradingEntityLeiGetter,
  makeTradingEntityNameGetter,
  negotiationPackageHasNotionalEquality,
  negotiationRowDataGetter,
  notionalHeaderValueGetter,
  repoClassificationFormatter
} from 'view/shared/datagrid'
import { LiveGridColumnId, NegotiationFieldNames } from '../../LiveNegotiationsGrid/LiveGridColumnNames'
import { NegotiableCellRenderer } from '../../LiveNegotiationsGrid/NegotiableCellRenderer'
import { TimeRenderer } from '../../LiveNegotiationsGrid/TimeRenderer'
import {
  getBondPriceModificationType,
  getEndDateModificationType,
  getHaircutModificationType,
  getHaircutTypeModificationType,
  getNotionalModificationType,
  getRateModificationType,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType,
  getStartDateModificationType,
  getStringModificationType
} from '../../shared/deltaGetters'
import { canSend, getLiveStatusColor, getNegotiatedValue, getNegotiationStatusAsString, isSummaryNegotiationType } from '../../shared/utils'
import { DecisionMakerHelper, MarketHelper, UserHelper } from 'model'
import { BooleanSelectbox, DecisionMakerSelectbox, OTWSelectbox } from 'view/shared/entity'
import { LiveAxeTradeConditionButtons } from '../../LiveNegotiationsGrid/LiveAxeTradeConditionButtons'
import { HaircutTypeSelectbox } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import { getTimeLeft } from 'utils'
import moment from 'moment'
import { incomingCurveNegotiationDetailsToTopLevelDetailsTransformerSelector } from './CurveNegotiationSelectors'
import { CurveNegotiationSelectorRenderer, DisabledSelector } from './CurveNegotiationSelectorRenderer'
import React from 'react'
import { LiveActionsCell } from '../../LiveNegotiationsGrid/LiveActionsCell'
import { withSelectedCurveItems } from './withSelectedCurveItems'
import { isNil } from 'lodash'
import { swallowPrecisionError } from 'utils/Decimal'
import {
  CounterpartyMultiLegCombobox,
  CounterpartyMultiLegComboboxProps
} from 'view/shared/entity/tradingEntity/CounterpartyMultiLegCombobox'
import { BASE_COLLECTION_TYPE, BASE_DIRECTION, BASE_REPO_TYPE } from 'view/shared/datagrid/baseColumns'
import { isNotNil } from 'utils/predicate'
import { CounterpartyFilter } from 'selectors'

const stopEventPropagation = (e: React.ClipboardEvent) => e.stopPropagation()

const COUNTERPARTY_NAME = {
  colId: LiveGridColumnId.COUNTERPARTY_NAME,
  valueGetter: makeTradingEntityNameGetter(false, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY),
  width: 150,
  filter: 'agSetColumnFilter',
  rowGroup: true,
  hide: true
}

const COLLECTION_ID = {
  colId: CollectionColumnId.COLLECTION_ID,
  headerName: Locale.translate(Translation.TC_COLLECTION_ID),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.collection?.id,
  filter: 'agSetColumnFilter',
  width: 150,
  hide: true
}

const COLLATERAL_NAME = {
  colId: LiveGridColumnId.COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.collateral?.name ?? rowData?.collateralBasket?.name
  },
  headerName: Locale.translate(Translation.TC_COLLATERAL),
  width: 120,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_ISIN_OR_BUI: ColDef = {
  colId: LiveGridColumnId.COLLATERAL_ISIN_OR_BUI,
  valueGetter: makeIsinGetter(negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_ISIN_BUI),
  width: 140,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_DIRECTION: ColDef = {
  ...BASE_DIRECTION,
  colId: LiveGridColumnId.NEGOTIATION_DIRECTION,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    if (!rowData) {
      return null
    }
    if (isMultiDirectionSummary(rowData)) {
      return SummaryRowDirection.MULTIPLE
    }
    return rowData && getOwnDirection(rowData.negotiation.direction, rowData.ownOrder)
  },
  headerName: Locale.translate(Translation.TC_ABBREVIATED_DIRECTION),
  cellRendererParams: {
    rowDataGetter: negotiationRowDataGetter
  }
}

const RFQ_TIMER: ColDef = {
  colId: LiveGridColumnId.RFQ_TIMER,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.negotiation.rfqEndTime,
  headerName: Locale.translate(Translation.TC_RFQ_TIME),
  equals: () => false,
  width: 84,
  minWidth: 84,
  cellRenderer: withAllContext(TimeRenderer),
  cellRendererParams: {
    which: 'rfq'
  },
  enableRowGroup: false
}

const COUNTERPARTY_CLEARING: ColDef = {
  colId: ClearingGridColumnId.COUNTERPARTY_CLEARING,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getClearingStatus(rowData)
  },
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_CLEARED),
  width: 70,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_CALLABLE: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CALLABLE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.negotiation.callableType,
  headerName: Locale.translate(Translation.TC_CALLABLE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: callableTypeFormatter,
  hide: true
}

const NEGOTIATION_CALL_DAYS: ColDef = {
  colId: LiveGridColumnId.NEGOTIATION_CALL_DAYS,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) =>
    negotiationRowDataGetter(params)?.negotiation.callablePeriodDays,
  headerName: Locale.translate(Translation.TC_CALL_DAYS),
  width: 100,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  enableRowGroup: false,
  hide: true
}

const REFERENCE_RATE_NAME: ColDef = {
  colId: LiveGridColumnId.REFERENCE_RATE_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.referenceRate?.name,
  headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
  width: 120,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEGOTIATION_STATUS = {
  colId: LiveGridColumnId.NEGOTIATION_STATUS,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getNegotiationStatusAsString(rowData)
  },
  headerName: Locale.translate(Translation.TC_STATUS),
  width: 140,
  filter: 'agSetColumnFilter',
  cellRenderer: StatusCellRenderer,
  cellRendererParams: {
    rowDataGetter: negotiationRowDataGetter,
    getStatusColor: (data: NegotiationDetail) => (canSend(data) ? constants.STATUS_COLOR_READY : getLiveStatusColor(data))
  },
  equals: () => false,
  lockVisible: true
}

export const NEGOTIATION_RATE = {
  colId: LiveGridColumnId.NEGOTIATION_RATE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getNegotiatedValue(rowData.negotiation.rate)
  },
  headerName: Locale.translate(Translation.TC_RATE),
  equals: () => false,
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeRateFilterGetter(),
  getQuickFilterText: makeRateQuickFilterTextGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getRateModificationType(negotiation.rate),
    validationField: NegotiationFieldNames.RATE,
    isRateField: true,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailRate,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      formatOptions: getRateFormatOptions(negotiationDetail.referenceRate),
      updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
      dataTestId: 'LiveNegotiationsGridModel-RateNumericInput'
    }),
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.RATE_NEGOTIATED,
    updateField: NegotiationFieldNames.RATE_MODIFIED,
    isModifiedField: NegotiationFieldNames.RATE_IS_MODIFIED,
    validationField: NegotiationFieldNames.RATE
  },
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_HAIRCUT = {
  colId: LiveGridColumnId.NEGOTIATION_HAIRCUT,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.haircut))
  },
  headerName: Locale.translate(Translation.TC_HAIRCUT),
  equals: () => false,
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeHaircutFormatter(),
  filterValueGetter: makeHaircutFilterGetter(),
  getQuickFilterText: makeHaircutQuickFilterTextGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getHaircutModificationType(negotiation.haircut),
    validationField: NegotiationFieldNames.HAIRCUT,
    rowDataGetter: negotiationRowDataGetter,
    dataTestId: 'LiveNegotiationsGridModel-HaircutNegotiableCellRenderer'
  },
  editable: canEditNegotiationDetailHaircut,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (negotiationDetail: NegotiationDetail, context: TypedContext<NegotiationPackageDetail>) => ({
      formatOptions: getHaircutFormatOptions(
        undefined,
        context.userPreference.haircutSign,
        negotiationDetail.ownOrder,
        negotiationDetail.direction
      ),
      updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
      onPaste: stopEventPropagation,
      dataTestId: 'LiveNegotiationsGridModel-HaircutNumericInput'
    }),
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.HAIRCUT_NEGOTIATED,
    updateField: NegotiationFieldNames.HAIRCUT_MODIFIED,
    isModifiedField: NegotiationFieldNames.HAIRCUT_IS_MODIFIED,
    validationField: NegotiationFieldNames.HAIRCUT
  },
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_HAIRCUT_TYPE = {
  colId: LiveGridColumnId.NEGOTIATION_HAIRCUT_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.haircutType))
  },
  valueFormatter: haircutTypeFormatter,
  headerName: Locale.translate(Translation.TC_COLLATERAL_HAIRCUT),
  equals: () => false,
  width: 150,
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.HAIRCUT_TYPE,
    rowDataGetter: negotiationRowDataGetter,
    getModificationType: ({ negotiation }: NegotiationDetail) => getHaircutTypeModificationType(negotiation.haircutType)
  },
  cellEditorParams: {
    inputComponent: HaircutTypeSelectbox,
    inputParams: (_negotiationDetail: NegotiationDetail) => ({
      onPaste: stopEventPropagation,
      canClear: true
    }),
    defaultField: NegotiationFieldNames.HAIRCUT_TYPE_NEGOTIATED,
    updateField: NegotiationFieldNames.HAIRCUT_TYPE_MODIFIED,
    isModifiedField: NegotiationFieldNames.HAIRCUT_TYPE_IS_MODIFIED,
    validationField: NegotiationFieldNames.HAIRCUT_TYPE
  },
  editable: canEditNegotiationDetailHaircutType,
  lockPinned: true,
  enableRowGroup: false
}

const OTW_DURATION = {
  colId: LiveGridColumnId.OTW_DURATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.negotiation.status === NegotiationStatus.QUOTED ? rowData.negotiation.otwEndTime : rowData?.otwDuration
  },
  headerName: Locale.translate(Translation.TC_OTW_TIME),
  equals: () => false,
  width: 84,
  minWidth: 84,
  cellRenderer: withAllContext(TimeRenderer),
  cellRendererParams: {
    which: 'otw'
  },
  cellEditorParams: {
    getNodeId: (data: NegotiationDetail) => data.negotiation.negotiationId,
    inputComponent: OTWSelectbox,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      remainingRFQTimeMins: moment.duration(getTimeLeft(negotiationDetail.negotiation.rfqEndTime)).asMinutes(),
      dataTestId: 'IncomingNegotiationsGridModel-OTWSelectbox',
      onPaste: stopEventPropagation
    }),
    updateField: NegotiationFieldNames.OTW_DURATION,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailOtw,
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_START_DATE = {
  colId: LiveGridColumnId.NEGOTIATION_START_DATE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startDate))
  },
  headerName: Locale.translate(Translation.TC_START_DATE),
  equals: () => false,
  width: 120,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getStartDateModificationType(negotiation.startDate),
    validationField: NegotiationFieldNames.START_DATE,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: false,
  lockPinned: true,
  enableRowGroup: false
}

export const makeMultipleNotionalFormatter = (formatter: ReturnType<typeof makeNotionalFormatter>) => (
  params: TypedValueFormatterParams<NegotiationPackageDetail, string, NegotiationPackageDetail>
) => {
  const { node, value } = params
  const rowData = negotiationRowDataGetter(params, false)

  if (node?.group || isNil(rowData)) {
    return ''
  }

  if (!negotiationPackageHasNotionalEquality(rowData)) {
    return value
  }
  return formatter(params)
}

const NEGOTIATION_MULTIPLE_NOTIONAL = {
  colId: LiveGridColumnId.NEGOTIATION_MULTIPLE_NOTIONAL,
  headerName: Locale.translate(Translation.TC_MULTIPLE_NOTIONAL),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params, false)
    if (params.node.group === true || isNil(rowData)) {
      return undefined
    }
    return negotiationPackageHasNotionalEquality(rowData)
      ? String(getNegotiatedValue(rowData.negotiation.notional))
      : Locale.translate(Translation.TC_MULTIPLE_NOTIONAL_MULTIPLE)
  },
  equals: () => false,
  width: 100,
  valueFormatter: makeMultipleNotionalFormatter(makeNotionalFormatter(false, negotiationRowDataGetter)),
  hide: false
}

const NEGOTIATION_NOTIONAL = {
  colId: LiveGridColumnId.NEGOTIATION_NOTIONAL,
  headerValueGetter: notionalHeaderValueGetter,
  headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.notional))
  },
  equals: () => false,
  width: 110,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeNotionalFilterGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getNotionalModificationType(negotiation.notional),
    validationField: NegotiationFieldNames.NOTIONAL,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailNotional,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
      min: negotiationDetail.collateral?.ticketIncrement,
      step: negotiationDetail.collateral?.ticketIncrement,
      formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat),
      onPaste: stopEventPropagation
    }),
    defaultField: NegotiationFieldNames.NOTIONAL_NEGOTIATED,
    updateField: NegotiationFieldNames.NOTIONAL_MODIFIED,
    isModifiedField: NegotiationFieldNames.NOTIONAL_IS_MODIFIED,
    validationField: NegotiationFieldNames.NOTIONAL
  },
  lockPinned: true,
  enableRowGroup: false,
  cellClassRules: {
    'paren-on-negative-cell': (params: any) => params.node.group && params.value > 0
  }
}

const NEGOTIATION_END_DATE = {
  colId: LiveGridColumnId.NEGOTIATION_END_DATE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.endDate))
  },
  headerName: Locale.translate(Translation.TC_END_DATE),
  equals: () => false,
  width: 120,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getEndDateModificationType(negotiation.endDate),
    validationField: NegotiationFieldNames.END_DATE,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: false,
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_BOND_PRICE = {
  colId: LiveGridColumnId.NEGOTIATION_BOND_PRICE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.bondPrice))
  },
  headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
  equals: () => false,
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({
    maxFractionDigits: constants.bondPriceMaxFractionDigits
  }),
  filterValueGetter: makeNumberFilterGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getBondPriceModificationType(negotiation.bondPrice),
    validationField: NegotiationFieldNames.BOND_PRICE,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailBondPrice,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      min: 0,
      maxLength: constants.bondPriceMaxFieldLength,
      formatOptions: {
        maxFractionDigits: constants.bondPriceMaxFractionDigits
      },
      onPaste: stopEventPropagation
    }),
    defaultField: NegotiationFieldNames.BOND_PRICE_NEGOTIATED,
    updateField: NegotiationFieldNames.BOND_PRICE_MODIFIED,
    isModifiedField: NegotiationFieldNames.BOND_PRICE_IS_MODIFIED,
    validationField: NegotiationFieldNames.BOND_PRICE
  },
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_ALL_IN_PRICE = {
  colId: LiveGridColumnId.NEGOTIATION_ALL_IN_PRICE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotSummary(rowData, rowData.negotiation.allInPrice)
  },
  headerName: Locale.translate(Translation.TC_ALL_IN_PRICE),
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  enableRowGroup: false
}

const NEGOTIATION_END_CONSIDERATION = {
  colId: LiveGridColumnId.NEGOTIATION_END_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.endConsideration)
  },
  headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false
}

const NEGOTIATION_REPO_INTEREST = {
  colId: LiveGridColumnId.NEGOTIATION_REPO_INTEREST,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.repoInterest)
  },
  headerName: Locale.translate(Translation.TC_REPO_INTEREST),
  width: 100,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatterSummaryRow({ minDp: 2 }, negotiationRowDataGetter, true),
  enableRowGroup: false,
  cellClassRules: {
    'paren-on-negative-cell': (params: any) => params.node.group && params.value > 0
  }
}

const NEGOTIATION_START_CONSIDERATION = {
  colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startConsideration))
  },
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
  equals: () => false,
  width: 110,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',

  valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
  filterValueGetter: makeStartConsiderationFilterGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: swallowPrecisionError(({ negotiation }: NegotiationDetail) =>
      getStartConsiderationModificationType(negotiation.startConsideration)
    ),
    validationField: NegotiationFieldNames.START_CONSIDERATION,
    rowDataGetter: negotiationRowDataGetter
  },
  editable: canEditNegotiationDetailStartConsideration,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (negotiationDetail: NegotiationDetail) => ({
      min: negotiationDetail.collateral?.ticketIncrement,
      step: negotiationDetail.collateral?.ticketIncrement,
      formatOptions: getStartConsiderationFormatOptions(),
      onPaste: stopEventPropagation
    }),
    defaultField: NegotiationFieldNames.START_CONSIDERATION_NEGOTIATED,
    updateField: NegotiationFieldNames.START_CONSIDERATION_MODIFIED,
    isModifiedField: NegotiationFieldNames.START_CONSIDERATION_IS_MODIFIED,
    validationField: NegotiationFieldNames.START_CONSIDERATION
  },
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_START_CONSIDERATION_OVERRIDE = {
  colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE,
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
  valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startConsiderationOverride))
  },
  valueFormatter: booleanFormatter,
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  headerClass: 'group-header-cell group-header-cell-right-align',
  headerComponentParams: {
    parent: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION
  },
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: swallowPrecisionError(({ negotiation }: NegotiationDetail) =>
      getStartConsiderationOverrideModificationType(negotiation.startConsiderationOverride)
    ),
    validationField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: BooleanSelectbox,
    defaultField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_NEGOTIATED,
    updateField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED,
    isModifiedField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_IS_MODIFIED,
    validationField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE
  },
  editable: canEditIncomingNegotiationDetailStartConsiderationOverride,
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_ACCRUED_INTEREST = {
  colId: LiveGridColumnId.NEGOTIATION_ACCRUED_INTEREST,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.accruedInterest)
  },
  headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
  width: 180,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatterSummaryRow({ minDp: 2 }, negotiationRowDataGetter),
  enableRowGroup: false
}

const NEGOTIATION_AXE_ROLL_UNWIND = {
  colId: LiveGridColumnId.NEGOTIATION_AXE_ROLL_UNWIND,
  headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.axeTradeCondition,
  equals: () => false,
  cellRenderer: withAllContext(createAxeTradeConditionRenderer(LiveAxeTradeConditionButtons)),
  keyCreator: axeTradeConditionKeyCreator,
  getQuickFilterText: axeTradeConditionFilterTextGetter,
  valueFormatter: axeTradeConditionFilterTextGetter,
  width: 130,
  filter: 'agSetColumnFilter',
  filterParams: { valueFormatter: axeFilterValueFormatter },
  sortable: false
}

const FUND_NAME = {
  colId: LiveGridColumnId.FUND_NAME,
  headerName: Locale.translate(Translation.TC_MY_ENTITY),
  valueGetter: makeTradingEntityNameGetter(true, negotiationRowDataGetter),
  equals: () => false,
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: ({ negotiation }: NegotiationDetail) => getStringModificationType(negotiation.counterpartyIdNegotiableField),
    validationField: NegotiationFieldNames.COUNTERPARTY_NAME,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: (params: { node: TypedRowNode<NegotiationDetail> }) => ({
    inputComponent: CounterpartyMultiLegCombobox,
    inputParams: (negotiationDetail: NegotiationDetail): Partial<CounterpartyMultiLegComboboxProps> => {
      const { negotiation } = negotiationDetail
      const isSummary = isSummaryNegotiationType(negotiation.negotiationType)

      const details =
        isSummary && params.node.group && params.node.allLeafChildren
          ? params.node.allLeafChildren.map(c => c.data).filter(isNotNil)
          : [negotiationDetail]

      const filters = details.map<CounterpartyFilter>(({ negotiation: n }) => ({
        isin: n.collateralId,
        bui: n.basketAllocation?.basketId ?? null,
        secondaryBasketId: n.secondaryBasketId && getNegotiatedValue(n.secondaryBasketId),
        secondaryCollateralId: n.secondaryCollateralId && getNegotiatedValue(n.secondaryCollateralId),
        fundId: n.fundId,
        repoType: n.repoType,
        masterLegalAgreementType: n.masterLegalAgreementType,
        marketType: n.marketType
      }))

      return {
        negotiationDetail,
        filters,
        dataTestId: 'IncomingNegotiationsGridModel-CounterpartyCombobox',
        onPaste: stopEventPropagation
      }
    },
    rowDataGetter: negotiationRowDataGetter,
    getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
    defaultField: NegotiationFieldNames.COUNTERPARTY_NAME_NEGOTIATED,
    updateField: NegotiationFieldNames.COUNTERPARTY_NAME_MODIFIED,
    isModifiedField: NegotiationFieldNames.COUNTERPARTY_NAME_IS_MODIFIED,
    validationField: NegotiationFieldNames.COUNTERPARTY_NAME
  }),
  editable: canEditNegotiationDetailCounterpartyId,
  width: 150,
  filter: 'agSetColumnFilter',
  enableRowGroup: false,
  lockPinned: true
}

const FUND_LEI = {
  colId: LiveGridColumnId.FUND_LEI,
  valueGetter: makeTradingEntityLeiGetter(true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_ENTITY_LEI),
  width: 170,
  filter: 'agSetColumnFilter'
}

const FUND_BIC = {
  colId: LiveGridColumnId.FUND_BIC,
  valueGetter: makeTradingEntityFieldGetter('bic', true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_ENTITY_BIC),
  width: 170,
  filter: 'agSetColumnFilter'
}

const INVESTMENT_DECISION_MAKER = {
  colId: LiveGridColumnId.INVESTMENT_DECISION_MAKER,
  headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.responderInvestmentDecisionMaker && DecisionMakerHelper.getName(rowData.responderInvestmentDecisionMaker)
  },
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: DecisionMakerSelectbox,
    inputParams: () => ({
      onPaste: stopEventPropagation
    }),
    defaultField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_NEGOTIATED,
    updateField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED,
    isModifiedField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_IS_MODIFIED,
    validationField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER
  },
  editable: true,
  lockPinned: true,
  enableRowGroup: false
}

const EXECUTION_DECISION_MAKER = {
  colId: LiveGridColumnId.EXECUTION_DECISION_MAKER,
  headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return rowData?.responderExecutionDecisionMaker && DecisionMakerHelper.getName(rowData.responderExecutionDecisionMaker)
  },
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER,
    rowDataGetter: negotiationRowDataGetter
  },
  cellEditorParams: {
    inputComponent: DecisionMakerSelectbox,
    inputParams: () => ({
      onPaste: stopEventPropagation
    }),
    defaultField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_NEGOTIATED,
    updateField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED,
    isModifiedField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_IS_MODIFIED,
    validationField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER
  },
  editable: true,
  lockPinned: true,
  enableRowGroup: false
}

const NEGOTIATION_ORDER_ID = {
  colId: LiveGridColumnId.NEGOTIATION_ORDER_ID,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotSummary(rowData, rowData?.negotiation.parentOrderId)
  },
  headerName: Locale.translate(Translation.TC_RFQ_ID),
  width: 100,
  filter: 'agSetColumnFilter'
}

const NEGOTIATION_PRIMARY_SALES_COVER = {
  colId: LiveGridColumnId.NEGOTIATION_PRIMARY_SALES_COVER,
  headerName: Locale.translate(Translation.TC_PRIMARY_SALES_COVER),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const user = negotiationRowDataGetter(params)?.counterpartyPrimarySalesCoverUser
    return user ? UserHelper.getName(user) : null
  },
  width: 180,
  filter: 'agSetColumnFilter'
}

const NEGOTIATION_SECONDARY_SALES_COVER = {
  colId: LiveGridColumnId.NEGOTIATION_SECONDARY_SALES_COVER,
  headerName: Locale.translate(Translation.TC_SECONDARY_SALES_COVER),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const user = negotiationRowDataGetter(params)?.counterpartySecondarySalesCoverUser
    return user ? UserHelper.getName(user) : null
  },
  width: 180,
  filter: 'agSetColumnFilter'
}

const NEGOTIATION_REPO_TYPE = {
  ...BASE_REPO_TYPE,
  colId: LiveGridColumnId.NEGOTIATION_REPO_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.negotiation.repoType
}

const FUND_FIRM = {
  colId: LiveGridColumnId.FUND_FIRM,
  valueGetter: makeOrganisationFieldGetter('legalName', true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_FIRM),
  width: 150,
  filter: 'agSetColumnFilter'
}

const FUND_FIRM_LEI = {
  colId: LiveGridColumnId.FUND_FIRM_LEI,
  valueGetter: makeOrganisationFieldGetter('lei', true, negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_MY_FIRM_LEI),
  width: 170,
  filter: 'agSetColumnFilter'
}

const COLLECTION_TYPE = {
  ...BASE_COLLECTION_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.collection?.type,
  hide: false
}

const NEGOTIATION_MARKET = {
  colId: LiveGridColumnId.NEGOTIATION_MARKET,
  headerName: Locale.translate(Translation.TC_MARKET),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const market = negotiationRowDataGetter(params)?.negotiation.market
    return market && MarketHelper.getName(market)
  },
  width: 80,
  filter: 'agSetColumnFilter'
}

const PRODUCT = {
  colId: LiveGridColumnId.PRODUCT,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.product?.name,
  headerName: Locale.translate(Translation.TC_PRODUCT),
  width: 80,
  filter: 'agSetColumnFilter'
}

const PRODUCT_SECTOR = {
  colId: LiveGridColumnId.PRODUCT_SECTOR,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.productSector?.name,
  headerName: Locale.translate(Translation.TC_SECTOR),
  width: 100,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_CUSIP = {
  colId: LiveGridColumnId.COLLATERAL_CUSIP,
  valueGetter: makeCusipGetter(negotiationRowDataGetter),
  headerName: Locale.translate(Translation.TC_CUSIP),
  width: 120,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_BASKET_DESCRIPTION = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
  headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) =>
    negotiationRowDataGetter(params)?.collateralBasket?.description,
  width: 220,
  filter: 'agSetColumnFilter'
}

export const COLLATERAL_COUPON = {
  colId: LiveGridColumnId.COLLATERAL_COUPON,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.collateral?.coupon,
  headerName: Locale.translate(Translation.TC_COUPON),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
  filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
  getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true }),
  enableRowGroup: false
}

const NEGOTIATION_TRADE_TYPE = {
  colId: LiveGridColumnId.NEGOTIATION_TRADE_TYPE,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
    const rowData = negotiationRowDataGetter(params)
    return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.repoClassification)
  },
  headerName: Locale.translate(Translation.TC_TRADE_TYPE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: repoClassificationFormatter
}

const MY_ENTITY_SHORT_NAME = {
  colId: LiveGridColumnId.MY_ENTITY_SHORT_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.orderFund?.shortName,
  headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const MY_FICC_ENTITY_SHORT_NAME = {
  colId: LiveGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
  valueGetter: (params: TypedValueGetterParams<NegotiationPackageDetail>) => negotiationRowDataGetter(params)?.orderFund?.ficcShortName,
  headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const ACTIONS = {
  colId: StandardGridColumnId.ACTIONS,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  equals: () => false,
  width: 240,

  minWidth: 240,
  cellRenderer: withAllContext(
    withSelectedCurveItems(
      createGroupedActionRenderer(LiveActionsCell, incomingCurveNegotiationDetailsToTopLevelDetailsTransformerSelector)
    )
  ),
  cellClass: 'action-cell',
  pinned: 'left' as const,
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  sortable: false
}

const CURVE_NEGOTIATION_PACKAGE_SELECTOR: ColDef = {
  colId: 'CurveSelector',
  headerName: '',
  headerComponent: DisabledSelector,
  equals: () => false,
  width: 35,
  minWidth: 35,
  cellClass: 'action-cell',
  enableRowGroup: false,
  lockVisible: true,
  filter: false,
  sortable: false,
  hide: false,
  lockPinned: true,
  lockPosition: true,
  pinned: 'left' as const,
  resizable: false,
  checkboxSelection: true
}

const CURVE_NEGOTIATION_SELECTOR: ColDef = {
  colId: 'CurveSelector',
  headerName: '',
  equals: () => false,
  width: 35,
  minWidth: 35,
  cellClass: () => 'action-cell',
  cellRenderer: withAllContext(CurveNegotiationSelectorRenderer),
  enableRowGroup: false,
  lockVisible: true,
  filter: false,
  sortable: false,
  resizable: false
}

export const CURVE_COLUMNS_COMMON_PREFIX: ColDef[] = [
  CURVE_NEGOTIATION_PACKAGE_SELECTOR,
  tradesGridGroupingRowColDef,
  ACTIONS,
  COUNTERPARTY_NAME,
  COLLECTION_ID,
  COLLATERAL_NAME,
  COLLATERAL_ISIN_OR_BUI,
  NEGOTIATION_DIRECTION,
  RFQ_TIMER,
  NEGOTIATION_MULTIPLE_NOTIONAL,
  COUNTERPARTY_CLEARING
]

export const CURVE_POSTFIX_GROUP_COLUMN_HEADER = 'Common Columns'
export const CURVE_COLUMNS_COMMON_POSTFIX = [
  NEGOTIATION_PRIMARY_SALES_COVER,
  NEGOTIATION_SECONDARY_SALES_COVER,
  NEGOTIATION_REPO_TYPE,
  FUND_FIRM,
  FUND_FIRM_LEI,
  COLLECTION_TYPE,
  NEGOTIATION_MARKET,
  PRODUCT,
  PRODUCT_SECTOR,
  COLLATERAL_CUSIP,
  COLLATERAL_BASKET_DESCRIPTION,
  COLLATERAL_COUPON,
  NEGOTIATION_TRADE_TYPE,
  MY_ENTITY_SHORT_NAME,
  MY_FICC_ENTITY_SHORT_NAME
]

export const CURVE_COLUMNS_TERM_GROUP = [
  NEGOTIATION_STATUS,
  CURVE_NEGOTIATION_SELECTOR,
  NEGOTIATION_RATE,
  NEGOTIATION_HAIRCUT,
  NEGOTIATION_HAIRCUT_TYPE,
  NEGOTIATION_NOTIONAL,
  NEGOTIATION_START_DATE,
  NEGOTIATION_END_DATE,
  OTW_DURATION,
  NEGOTIATION_BOND_PRICE,
  NEGOTIATION_ALL_IN_PRICE,
  {
    marryChildren: true,
    children: [NEGOTIATION_START_CONSIDERATION, NEGOTIATION_START_CONSIDERATION_OVERRIDE]
  },
  NEGOTIATION_REPO_INTEREST,
  NEGOTIATION_ACCRUED_INTEREST,
  NEGOTIATION_END_CONSIDERATION,
  NEGOTIATION_AXE_ROLL_UNWIND,
  NEGOTIATION_CALLABLE,
  NEGOTIATION_CALL_DAYS,
  REFERENCE_RATE_NAME,
  FUND_NAME,
  FUND_LEI,
  FUND_BIC,
  INVESTMENT_DECISION_MAKER,
  EXECUTION_DECISION_MAKER,
  NEGOTIATION_ORDER_ID
]

export const curveGridOptions: GridOptions = {
  ...gridOptionsBase,
  defaultColDef: {
    ...gridOptionsBase.defaultColDef,
    enableRowGroup: false
  },
  groupHeaderHeight: 20,
  headerHeight: 20,
  groupDefaultExpanded: 1,
  getRowId: ({ data }) => data.uuid
}
